import dayjs from 'dayjs';

const FRECUENCIA_LABEL = {
  mensual: 'Mensual',
  quincenal: 'Quincenal',
  semanal: 'Semanal',
  diario: 'Diario',
};

const normalizeFrecuencia = (frecuencia) => {
  if (!frecuencia) return 'Mensual';
  const key = String(frecuencia).toLowerCase();
  return FRECUENCIA_LABEL[key] || 'Mensual';
};

const ratePerPeriod = (tasaAnualPct, frecuencia, cuotas) => {
  const t = (Number(tasaAnualPct) || 0) / 100;
  switch (frecuencia) {
    case 'Semanal':
      return t / 4;
    case 'Quincenal':
      return t / 2;
    case 'Diario':
      return cuotas > 0 ? t / cuotas : 0;
    case 'Mensual':
    default:
      return t / 1;
  }
};

const stepDate = (date, frecuencia) => {
  switch (frecuencia) {
    case 'Diario':
      return date.add(1, 'day');
    case 'Semanal':
      return date.add(7, 'day');
    case 'Quincenal':
      return date.add(15, 'day');
    case 'Mensual':
    default:
      return date.add(1, 'month');
  }
};

const buildQuincenalDates = (firstCuotaDate, dia1, dia2, count) => {
  const a = Math.min(Number(dia1), Number(dia2));
  const b = Math.max(Number(dia1), Number(dia2));
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === b) return null;

  // Para que la primera fecha generada coincida con la primera cuota existente,
  // arrancamos desde un día antes para que sea inclusiva en el chequeo isAfter/isSame.
  const start = dayjs(firstCuotaDate).startOf('day').subtract(1, 'day');
  const candidates = [];
  let y = start.year();
  let m = start.month();
  for (let guard = 0; guard < 800 && candidates.length < count + 5; guard++) {
    const dim = dayjs(new Date(y, m, 1)).daysInMonth();
    for (const day of [a, b]) {
      const dd = Math.min(day, dim);
      const dt = dayjs(new Date(y, m, dd)).startOf('day');
      if (dt.isAfter(start) || dt.isSame(start, 'day')) {
        candidates.push(dt);
      }
    }
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return candidates
    .sort((x, z) => x.valueOf() - z.valueOf())
    .slice(0, count);
};

const generateDates = ({ firstCuotaDate, frecuencia, cuotas, diasPagoQuincenal }) => {
  if (frecuencia === 'Quincenal' && diasPagoQuincenal && diasPagoQuincenal.dia1 != null && diasPagoQuincenal.dia2 != null) {
    const dates = buildQuincenalDates(firstCuotaDate, diasPagoQuincenal.dia1, diasPagoQuincenal.dia2, cuotas);
    if (dates && dates.length === cuotas) return dates;
  }
  const out = [];
  let d = dayjs(firstCuotaDate);
  for (let i = 0; i < cuotas; i++) {
    out.push(d);
    d = stepDate(d, frecuencia);
  }
  return out;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Genera una vista previa de la nueva tabla de amortización en frontend
 * usando la misma lógica de cálculo del backend.
 *
 * @param {Object} params
 * @param {string} params.metodo - 'fija' | 'fijo' | 'flat' | 'insoluto'
 * @param {number|string} params.montoSolicitado
 * @param {number|string} params.interes - tasa por periodo en porcentaje (mismo formato que backend)
 * @param {number|string} params.plazo - número de cuotas
 * @param {string} params.frecuencia - 'Mensual' | 'Quincenal' | 'Semanal' | 'Diario' (case-insensitive)
 * @param {Date} params.firstCuotaDate - fecha de la primera cuota (referencia)
 * @param {Object} [params.diasPagoQuincenal] - { dia1, dia2 } cuando frecuencia=Quincenal con días fijos
 * @returns {Array<{numero_cuota, fecha, capital, interes, cuota}>}
 */
export function previewAmortizacion({
  metodo,
  montoSolicitado,
  interes,
  plazo,
  frecuencia,
  firstCuotaDate,
  diasPagoQuincenal,
}) {
  const cuotas = parseInt(plazo, 10);
  const principal = Number(montoSolicitado) || 0;

  if (!Number.isFinite(cuotas) || cuotas <= 0) return [];
  if (!firstCuotaDate || principal <= 0) return [];

  const freq = normalizeFrecuencia(frecuencia);
  const r = ratePerPeriod(interes, freq, cuotas);
  const dates = generateDates({ firstCuotaDate, frecuencia: freq, cuotas, diasPagoQuincenal });
  if (!dates || dates.length !== cuotas) return [];

  const m = String(metodo || '').toLowerCase();

  if (m === 'fija' || m === 'fijo' || m === 'flat') {
    const capitalUnit = principal / cuotas;
    const interesUnit = principal * r;
    const cuotaTotal = (principal * r * cuotas + principal) / cuotas;
    const out = [];
    let saldo = principal;
    for (let i = 0; i < cuotas; i++) {
      let capital = capitalUnit;
      if (i === cuotas - 1) capital = saldo;
      const capitalR = round2(capital);
      const interesR = round2(interesUnit);
      const cuotaR = round2(cuotaTotal);
      saldo = round2(saldo - capitalR);
      if (saldo < 0) saldo = 0;
      out.push({
        numero_cuota: i + 1,
        fecha: dates[i].format('DD/MM/YYYY'),
        capital: capitalR.toFixed(2),
        interes: interesR.toFixed(2),
        cuota: cuotaR.toFixed(2),
      });
    }
    return out;
  }

  if (m === 'insoluto') {
    const capitalFijo = principal / cuotas;
    let saldo = principal;
    const out = [];
    for (let i = 0; i < cuotas; i++) {
      let capital = capitalFijo;
      const interesPeriodo = saldo * r;
      let cuotaFinal = capital + interesPeriodo;
      if (i === cuotas - 1) {
        capital = saldo;
        cuotaFinal = capital + interesPeriodo;
      }
      const capitalR = round2(capital);
      const interesR = round2(interesPeriodo);
      const cuotaR = round2(cuotaFinal);
      out.push({
        numero_cuota: i + 1,
        fecha: dates[i].format('DD/MM/YYYY'),
        capital: capitalR.toFixed(2),
        interes: interesR.toFixed(2),
        cuota: cuotaR.toFixed(2),
      });
      saldo = round2(saldo - capitalR);
      if (saldo < 0) saldo = 0;
    }
    return out;
  }

  return [];
}

/**
 * Deriva los días de pago quincenal a partir de las fechas de las dos primeras
 * cuotas existentes. Devuelve null si no se pueden inferir o son iguales.
 */
export function deriveDiasPagoQuincenal(amortizacion) {
  if (!Array.isArray(amortizacion) || amortizacion.length < 2) return null;
  const parseDay = (fechaStr) => {
    if (!fechaStr || typeof fechaStr !== 'string') return null;
    const parts = fechaStr.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    return Number.isFinite(day) ? day : null;
  };
  const dia1 = parseDay(amortizacion[0]?.fecha);
  const dia2 = parseDay(amortizacion[1]?.fecha);
  if (!Number.isFinite(dia1) || !Number.isFinite(dia2) || dia1 === dia2) return null;
  return { dia1, dia2 };
}
